import { isMobileOnly } from "react-device-detect";
import { LoaderFunctionArgs, redirect } from "react-router";
import { loadAirportsFromItineraryId } from "v2/funnels/pre-eligibility/features/airportsFromItineraryId";
import { loadAirportsFromQueryParams } from "v2/funnels/pre-eligibility/features/airportsFromQueryParams";
import { setFirstStep } from "v2/funnels/pre-eligibility/state/appState";

const basePath = "/claims/new";
const baseMobilePath = `${basePath}/mobile`;
const desktopFirstStep = `${basePath}/trip-details`;
const mobileFirstStep = `${baseMobilePath}/trip-details`;
const enableMobileAppEntry = import.meta.env.VITE_ENABLE_MOBILE_APP_ENTRY === "true";

function redirectToFirstStepWith(searchParams: URLSearchParams): string {
  const searchParamsString = searchParams.size > 0 ? `?${searchParams}` : "";

  return `${isMobileOnly ? mobileFirstStep : desktopFirstStep}${searchParamsString}`;
}

function mobileSkipToStep(
  currentLocation: string,
  step: string,
  searchParams: URLSearchParams,
): string | null {
  if (currentLocation.startsWith(`${baseMobilePath}/${step}`)) {
    setFirstStep(step);
    return null;
  }
  return `${baseMobilePath}/${step}?${searchParams}`;
}

/**
 * Resolves the appropriate landing path for users entering the funnel.
 *
 * 1. Redirects all base paths (e.g., /claims/new or /claims/new/mobile) to the first step
 *    based on the user's device, including the provided query parameters.
 *
 * 2. Implements skip-step logic for mobile devices. For more details, refer to:
 *    https://airhelp.atlassian.net/wiki/spaces/ST/pages/370016658/Pre-eligibility+Funnel+Flow+review#Mid-landing-provided-in-params
 *
 * 3. Ensures the user is directed to the correct first step based on their device
 *    (mobile or desktop).
 */

async function resolveFirstLocation(pathname: string, searchParams: URLSearchParams) {
  if (pathname === basePath || pathname === baseMobilePath) {
    return redirectToFirstStepWith(searchParams);
  }

  const skipToStepLocation = await resolveSkipToStepLocation(searchParams);
  if (skipToStepLocation) {
    return mobileSkipToStep(pathname, skipToStepLocation, searchParams);
  }

  const isAtCorrectFirstStep =
    (isMobileOnly && pathname === mobileFirstStep) ||
    (!isMobileOnly && pathname === desktopFirstStep);

  if (!isAtCorrectFirstStep) {
    return redirectToFirstStepWith(searchParams);
  }

  return null;
}

export async function preEligibilityLoaderV2({
  request,
}: LoaderFunctionArgs): Promise<null | ResponseInit> {
  const url = new URL(request.url);
  const { pathname, searchParams } = url;

  const location = await resolveFirstLocation(pathname, searchParams);

  return location ? redirect(location) : null;
}

async function resolveSkipToStepLocation(searchParams: URLSearchParams): Promise<string | null> {
  const itineraryId = searchParams.get("itinerary_id");
  let airports;

  if (
    itineraryId !== null &&
    (Number(itineraryId) || itineraryId === "0") &&
    enableMobileAppEntry
  ) {
    airports = await loadAirportsFromItineraryId(itineraryId);
  } else {
    airports = await loadAirportsFromQueryParams();
  }
  const { departureAirport, arrivalAirport, hasConnections } = airports;

  if (isMobileOnly && departureAirport && arrivalAirport && hasConnections) {
    return "multileg-disrupted-flight";
  }
  return null;
}
